import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl, toUploadUrl } from '../config/api';

const fallbackSlides = [
  {
    id: 1,
    image: toUploadUrl('1773139773239-402424749.jpg'),
    title: 'Discover the Beauty of Kashmir',
    subtitle: 'Experience paradise on earth',
    description: 'Crystal clear lakes, snow-capped mountains, and lush green valleys await you',
    ctaText: 'Explore Packages',
    ctaLink: '/packages'
  },
  {
    id: 2,
    image: toUploadUrl('dal-lake.png'),
    title: 'Dal Lake - Jewel of Kashmir',
    subtitle: 'Stay in luxurious houseboats',
    description: 'Experience the magic of floating on Dal Lake with our exclusive houseboat stays',
    ctaText: 'View Houseboats',
    ctaLink: '/packages?type=houseboat'
  },
  {
    id: 3,
    image: toUploadUrl('Gulmarg.png'),
    title: 'Gulmarg - Meadow of Flowers',
    subtitle: 'Asia\'s highest gondola',
    description: 'Ski on pristine slopes or take the cable car to Apharwat Peak for breathtaking views',
    ctaText: 'Discover Gulmarg',
    ctaLink: '/destinations/14'
  }
];

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [slides, setSlides] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchSlides = async () => {
      try {
        const response = await axios.get(buildApiUrl('/hero-slides'));
        const slidesData = Array.isArray(response.data) ? response.data : [];

        const normalizedSlides = slidesData
          .filter((slide) => slide?.title && slide?.image)
          .map((slide) => ({
            id: slide.id,
            image: toUploadUrl(slide.image),
            title: slide.title,
            subtitle: slide.subtitle || '',
            description: slide.description || '',
            ctaText: slide.cta_text || 'Explore Now',
            ctaLink: slide.cta_link || '/packages'
          }));

        if (isMounted) {
          setSlides(normalizedSlides.length > 0 ? normalizedSlides : fallbackSlides);
        }
      } catch (error) {
        console.error('Failed to load hero slides:', error);
        if (isMounted) {
          setSlides(fallbackSlides);
        }
      }
    };

    fetchSlides();

    return () => {
      isMounted = false;
    };
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (!slides.length) return undefined;

    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  useEffect(() => {
    if (currentSlide >= slides.length && slides.length > 0) {
      setCurrentSlide(0);
    }
  }, [currentSlide, slides.length]);

  if (!slides.length) {
    return null;
  }

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  return (
    <section className="hero-slider">
      {/* Slides */}
     <div className="slides-container">
  {slides.map((slide, index) => (
    <div
      key={slide.id}
      className={`slide ${index === currentSlide ? 'active' : ''}`}
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${slide.image})`
      }}
    >
      <div className="slide-content" key={`content-${currentSlide}`}>
        <span className="slide-subtitle">{slide.subtitle}</span>
        <h1 className="slide-title">{slide.title}</h1>
        <p className="slide-description">{slide.description}</p>
        <Link to={slide.ctaLink} className="slide-cta">
          {slide.ctaText} <i className="fas fa-arrow-right"></i>
        </Link>
      </div>
    </div>
  ))}
</div>
      {/* Navigation Arrows */}
      <button className="slider-nav prev" onClick={prevSlide}>
        <i className="fas fa-chevron-left"></i>
      </button>
      <button className="slider-nav next" onClick={nextSlide}>
        <i className="fas fa-chevron-right"></i>
      </button>

      {/* Dots/Pagination */}
      <div className="slider-dots">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>

      {/* Pause/Play Button */}
      <button 
        className="slider-control"
        onClick={() => setIsAutoPlaying(!isAutoPlaying)}
      >
        <i className={`fas fa-${isAutoPlaying ? 'pause' : 'play'}`}></i>
      </button>

      {/* CSS Styles - Moved to separate style tag without jsx attribute */}
      <style>{`
        .hero-slider {
          position: relative;
          width: 100%;
          height: 80vh;
          min-height: 600px;
          overflow: hidden;
        }

        .slides-container {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          opacity: 0;
          transition: opacity 1s ease-in-out;
          display: flex;
          align-items: center;
          justify-content: center;
            pointer-events: none; /* inactive slides click block karo */

        }

        .slide.active {
          opacity: 1;
            pointer-events: all; /* sirf active slide clickable */

        }

        .slide-content {
          text-align: center;
          color: white;
          max-width: 800px;
          padding: 2rem;
          transform: translateY(30px);
          opacity: 0;
          animation: slideUp 1s ease-out forwards;
          animation-delay: 0.5s;
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .slide-subtitle {
          display: inline-block;
          font-size: 1.2rem;
          text-transform: uppercase;
          letter-spacing: 3px;
          margin-bottom: 1rem;
          padding: 0.5rem 1.5rem;
          background: rgba(255,255,255,0.2);
          border-radius: 50px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.3);
        }

        .slide-title {
          font-size: 4rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          line-height: 1.2;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }

        .slide-description {
          font-size: 1.2rem;
          margin-bottom: 2rem;
          line-height: 1.8;
          opacity: 0.95;
        }

        .slide-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 2.5rem;
          background: linear-gradient(135deg, #ff6b35, #ff4f1a);
          color: white;
          text-decoration: none;
          border-radius: 50px;
          font-weight: 600;
          font-size: 1.1rem;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }

        .slide-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(255,107,53,0.3);
        }

        .slide-cta i {
          transition: transform 0.3s ease;
        }

        .slide-cta:hover i {
          transform: translateX(5px);
        }

        /* Navigation Arrows */
        .slider-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 50px;
          height: 50px;
          background: rgba(255,255,255,0.2);
          border: 2px solid rgba(255,255,255,0.5);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
          backdrop-filter: blur(5px);
        }

        .slider-nav:hover {
          background: white;
          color: #ff6b35;
          border-color: white;
        }

        .slider-nav.prev {
          left: 30px;
        }

        .slider-nav.next {
          right: 30px;
        }

        /* Dots/Pagination */
        .slider-dots {
          position: absolute;
          bottom: 30px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 10px;
          z-index: 10;
        }

        .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255,255,255,0.5);
          border: 2px solid rgba(255,255,255,0.8);
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 0;
        }

        .dot.active {
          background: white;
          transform: scale(1.3);
          border-color: #ff6b35;
        }

        /* Pause/Play Button */
        .slider-control {
          position: absolute;
          bottom: 30px;
          right: 30px;
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.2);
          border: 2px solid rgba(255,255,255,0.5);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
          backdrop-filter: blur(5px);
        }

        .slider-control:hover {
          background: white;
          color: #ff6b35;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .hero-slider {
            min-height: 500px;
          }
          
          .slide-title {
            font-size: 3rem;
          }
        }

        @media (max-width: 768px) {
          .hero-slider {
            min-height: 450px;
          }
          
          .slide-title {
            font-size: 2.5rem;
          }

          .slide-description {
            font-size: 1rem;
          }

          .slider-nav {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }

          .slider-nav.prev {
            left: 15px;
          }

          .slider-nav.next {
            right: 15px;
          }
          
          .slide-subtitle {
            font-size: 0.9rem;
            padding: 0.3rem 1rem;
          }
        }

        @media (max-width: 480px) {
          .hero-slider {
            min-height: 400px;
          }
          
          .slide-title {
            font-size: 2rem;
          }

          .slide-subtitle {
            font-size: 0.8rem;
          }

          .slide-cta {
            padding: 0.8rem 1.5rem;
            font-size: 0.9rem;
          }

          .slider-dots {
            bottom: 20px;
          }

          .slider-control {
            bottom: 20px;
            right: 20px;
            width: 35px;
            height: 35px;
          }
          
          .slide-description {
            font-size: 0.9rem;
            margin-bottom: 1.5rem;
          }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
